import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	useMemo,
} from "react";
import { useParams } from "react-router-dom";

// mui components
import { Box, Fade, Stack, Tab, Tabs } from "@mui/material";

// mui icons
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

// api slice
import { useEnhancedConditionsEfolderDocumentsListMutation } from "../../../features/enhanced_conditions_efolder_documents_list/fetchEnhancedConditionsEfolderDocumentsListApiSlice";
import { useEnhancedConditionsDocumentsListMutation } from "../../../features/enhanced-conditions-documents_list/fetchEnhancedConditionsDocumentsListApiSlice";
import { useEnhancedConditionAttachmentsPendingRetryMutation } from "../../../features/enhanced-condition-attachments-pending-retry/fetchEnhancedConditionAttachmentsPendingRetryApiSlice";
import { useStandardDocumentUploadRetriesPendingRetryMutation } from "../../../features/standard-document-upload-retries-pending-retry/fetchStandardDocumentUploadRetriesPendingRetryApiSlice";
import { useExportJobMutation } from "../../../features/create-export-job/createExportJobApiSlice";
import { useJobStatusMutation } from "../../../features/export-job-status/exportJobStatusApiSlice";
import { useRetryPendingDocumentUploadsMutation } from "../../../features/retry-pending-document-uploads/retryPendingDocumentUploadsApiSlice";

// custom components
import Heading3 from "../../typography/heading-03";
import Body2 from "../../typography/body-02";
import PrimaryButton from "../../buttons/primary-button";
import TertiaryButton from "../../buttons/tertiary-button";
import OutlinedTextInput from "../../custom-input/outlined-text-input";
import CustomLoader from "../../loader";
import CustomTable from "../../custom-table";
import ConditionTrackingCard from "../condition-tracking";
import DocumentInfoModal from "../../modal/document-info-modal";
import CustomAlert from "../../custom-alert";
import {
	getTrackerDocumentSelectionId,
	getTrackerMatchingConditionInfo,
} from "../../custom-table/table-row/conditions-document-tracker";

// redux functions
import { getEnhancedConditionsDocumentsList } from "../../../app/services/enhanced-conditions-documents-list";
import { sortDate } from "../../../utils/sort-methods";
import {
	getDocumentPipelineStatusPresentation,
	isProcessingSummaryStatus,
} from "../../../utils/document-pipeline-status";

// custom styles
import styles from "./index.module.scss";

const hasAssignedTo = (item) => {
	if (Array.isArray(item?.assignedTo)) {
		return item.assignedTo.length > 0;
	}

	return Boolean(item?.assignedTo);
};

// Single source of truth for the "Fully Received" summary count: reuses the
// exact same composition (getTrackerMatchingConditionInfo + hasAssignedTo +
// getDocumentPipelineStatusPresentation) already used to render each row's
// own status pill (conditions-document-tracker.jsx) and to sort the "Logan
// Status" column (custom-table/index.jsx), instead of a separate ad-hoc rule.
const isFullyReceivedByLogan = (row, conditionDocuments) => {
	const {
		matchingConditionCount,
		hasMatchingAttachment,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
	} = getTrackerMatchingConditionInfo(row, conditionDocuments);

	const { loganStatusText } = getDocumentPipelineStatusPresentation({
		row,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
		matchingConditionCount,
		hasMatchingAttachment,
		hasAssignedTo: hasAssignedTo(row),
		isPendingRetryRecord: Boolean(row?.__isPendingRetryRecord),
	});

	return loganStatusText === "Fully Received By Logan";
};

const normalizeSearchText = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase();

const isProcessingSummaryDocument = (
	item,
	conditionDocuments = []
) => {
	if (item?.__isProcessingRow) {
		return true;
	}

	const {
		matchingConditionCount,
		hasMatchingAttachment,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
	} = getTrackerMatchingConditionInfo(item, conditionDocuments);

	const hasAssignedTo = Array.isArray(item?.assignedTo)
		? item.assignedTo.length > 0
		: Boolean(item?.assignedTo);

	const { pipelineStatusText } = getDocumentPipelineStatusPresentation({
		row: item,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
		matchingConditionCount,
		hasMatchingAttachment,
		hasAssignedTo,
		isPendingRetryRecord: Boolean(item?.__isPendingRetryRecord),
	});

	return isProcessingSummaryStatus({
		pipelineStatusText,
	});
};

const normalizeExactText = (value) => String(value ?? "").trim();

const firstNonEmpty = (...values) => {
	for (const value of values) {
		const text = String(value ?? "").trim();
		if (text) return text;
	}

	return "";
};

const getMatchingConditionTitles = (row, conditionDocuments = []) => {
	const rowId = String(row?.id ?? "");
	return conditionDocuments
		.filter((doc) =>
			Array.isArray(doc?.attachments)
				? doc.attachments.some((attachment) =>
						[attachment?.entityId]
							.filter(Boolean)
							.some((value) => String(value) === rowId)
					)
				: false
		)
		.map((doc) =>
			firstNonEmpty(
				doc?.title,
				doc?.documentName,
				doc?.conditionTitle,
				doc?.linkedConditionTitle,
				doc?.name,
				doc?.fileName
			)
		)
		.filter(Boolean)
		.join(" ");
};

const buildDocumentSearchHaystack = (row, conditionDocuments = []) =>
	[
		row?.title,
		row?.documentName,
		row?.fileName,
		row?.name,
		row?.encompassDocumentName,
		row?.encompassDocName,
		row?.document_title,
		row?.conditionTitle,
		row?.linkedConditionTitle,
		getMatchingConditionTitles(row, conditionDocuments),
	]
		.map(normalizeSearchText)
		.join(" ");

const getEffectiveConditionType = (item, selectedConditionTypes = {}) => {
	const rowId = item?.entityId ?? item?.id;
	return selectedConditionTypes?.[rowId] ?? item?.conditionType ?? "";
};

const getDocumentsByTab = (
	documents,
	tabKey,
	selectedConditionTypes = {},
	getMatchingConditionCount = () => 0
) => {
	if (tabKey === "in-progress") {
		return documents?.filter(
			(item) =>
				String(
					getEffectiveConditionType(item, selectedConditionTypes)
				).trim() === "" && !hasAssignedTo(item)
		);
	}

	if (tabKey === "has-conditions") {
		return documents?.filter(
			(item) => Number(getMatchingConditionCount(item) ?? 0) > 0
		);
	}

	return documents;
};

const formatEasternTimestamp = (date = new Date()) =>
	new Intl.DateTimeFormat("en-US", {
		timeZone: "America/New_York",
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	}).format(date) + " ET";

const sortDocumentsByCreatedDateDesc = (documents = []) =>
	sortDate([...(documents ?? [])], "desc", "createdDate");

const getTrackerDocumentFileName = (document) =>
	firstNonEmpty(
		document?.fileName,
		document?.title,
		document?.entityName,
		document?.documentName
	);

const getTrackerDocumentUploadedByValue = (document) =>
	firstNonEmpty(
		document?.uploaded_by,
		document?.uploadedBy,
		document?.createdBy?.entityId
	);

const getFirstConditionMapping = (record = {}) =>
	Array.isArray(record?.condition_mappings)
		? record.condition_mappings.find(Boolean) ?? {}
		: {};

const getConditionSummaryValues = (conditionMappings = [], key) =>
	conditionMappings
		.map((mapping) => firstNonEmpty(mapping?.[key]))
		.filter(Boolean);

const getTrackerDocumentStatusText = (document, conditionDocuments = []) => {
	const {
		matchingConditionCount,
		hasMatchingAttachment,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
	} = getTrackerMatchingConditionInfo(document, conditionDocuments);

	const { loganStatusText } = getDocumentPipelineStatusPresentation({
		row: document,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
		matchingConditionCount,
		hasMatchingAttachment,
		hasAssignedTo: hasAssignedTo(document),
		isPendingRetryRecord: Boolean(document?.__isPendingRetryRecord),
	});

	return normalizeExactText(loganStatusText);
};

const filterDuplicateFileNameDocuments = (
	documents = [],
	conditionDocuments = []
) => {
	const groupedDocuments = new Map();

	(documents ?? []).forEach((document) => {
		const fileName = normalizeExactText(getTrackerDocumentFileName(document));
		const uploadedBy = normalizeExactText(
			getTrackerDocumentUploadedByValue(document)
		);

		if (!fileName || !uploadedBy) {
			return;
		}

		const groupKey = `${fileName}__${uploadedBy}`;
		const group = groupedDocuments.get(groupKey) ?? [];
		group.push(document);
		groupedDocuments.set(groupKey, group);
	});

	return (documents ?? []).filter((document) => {
		const fileName = normalizeExactText(getTrackerDocumentFileName(document));
		const uploadedBy = normalizeExactText(
			getTrackerDocumentUploadedByValue(document)
		);

		if (!fileName || !uploadedBy) {
			return true;
		}

		const groupKey = `${fileName}__${uploadedBy}`;
		const group = groupedDocuments.get(groupKey) ?? [];

		if (group.length < 2) {
			return true;
		}

		const statusTexts = new Set(
			group.map((item) => getTrackerDocumentStatusText(item, conditionDocuments))
		);

		const hasPendingUpdate = statusTexts.has("Pending Update To Loan File");
		const hasFullyReceived = statusTexts.has("Fully Received By Logan");

		if (!hasPendingUpdate || !hasFullyReceived) {
			return true;
		}

		return (
			getTrackerDocumentStatusText(document, conditionDocuments) !==
			"Pending Update To Loan File"
		);
	});
};

const getTrackerDocumentKey = (document) =>
	String(
		document?.entityId ??
			document?.id ??
			document?.attachmentId ??
			document?.uploadKey ??
			document?.__stableUploadKey ??
			""
	).trim();

const getTrackerDocumentFingerprint = (document) =>
	`${normalizeSearchText(
		document?.fileName ?? document?.entityName ?? document?.title
	)}|${String(document?.fileSize ?? document?.size ?? "").trim()}`;

const buildOptimisticTrackerDocument = (uploadProgressInfo = {}) => {
	const uploadKey = getTrackerDocumentKey(uploadProgressInfo);
	const uploadedAt =
		uploadProgressInfo?.createdDate ?? new Date().toISOString();

	return {
		...uploadProgressInfo,
		__isProcessingRow: true,
		__stableUploadKey: uploadKey,
		id: uploadKey,
		entityId: uploadKey,
		title: uploadProgressInfo?.fileName ?? uploadProgressInfo?.title ?? "-",
		fileName: uploadProgressInfo?.fileName ?? uploadProgressInfo?.title ?? "-",
		entityName:
			uploadProgressInfo?.fileName ?? uploadProgressInfo?.title ?? "-",
		createdDate: uploadedAt,
		updatedDate: uploadedAt,
		assignedTo: uploadProgressInfo?.assignedTo ?? [],
		createdBy:
			uploadProgressInfo?.createdBy ??
			(uploadProgressInfo?.uploadedBy
				? { entityId: uploadProgressInfo.uploadedBy }
				: undefined),
	};
};

const buildPendingRetryTrackerDocument = (record = {}, source = "enhanced") => {
	const conditionMappings = Array.isArray(record?.condition_mappings)
		? record.condition_mappings.filter(Boolean)
		: [];
	const firstConditionMapping = getFirstConditionMapping(record);
	const recordId = String(
		record?.id ??
			record?.entity_id ??
			record?.attachment_filename ??
			firstConditionMapping?.condition_id ??
			firstConditionMapping?.document_id ??
			`${source}-pending-retry`
	).trim();
	const title = firstNonEmpty(
		record?.attachment_filename,
		record?.original_file_name,
		record?.document_name,
		record?.title,
		firstConditionMapping?.document_name,
		firstConditionMapping?.condition_name
	);
	const createdDate =
		record?.created_at ??
		record?.createdAt ??
		firstConditionMapping?.created_at ??
		new Date().toISOString();
	const conditionIds = getConditionSummaryValues(
		conditionMappings,
		"condition_id"
	);
	const conditionNames = getConditionSummaryValues(
		conditionMappings,
		"condition_name"
	);
	const conditionTypes = getConditionSummaryValues(
		conditionMappings,
		"condition_type"
	);
	const primaryConditionId =
		record?.condition_id ?? firstConditionMapping?.condition_id ?? "";
	const primaryConditionName =
		record?.condition_name ??
		firstConditionMapping?.condition_name ??
		conditionNames[0] ??
		"";
	const primaryConditionType =
		record?.condition_type ??
		firstConditionMapping?.condition_type ??
		conditionTypes[0] ??
		"";
	const primaryDocumentId =
		record?.document_id ??
		record?.documentId ??
		firstConditionMapping?.document_id ??
		firstConditionMapping?.efolder_id ??
		recordId;
	const primaryEntityId =
		record?.entity_id ??
		record?.entityId ??
		recordId;
	const primaryEntityType =
		record?.entity_type ??
		record?.entityType ??
		firstConditionMapping?.entity_type ??
		"Document";
	const primaryEfolderId =
		record?.efolder_id ??
		record?.efolderId ??
		firstConditionMapping?.efolder_id ??
		firstConditionMapping?.document_id ??
		"";

	return {
		__isPendingRetryRecord: true,
		__pendingRetryKind: source,
		__pendingRetryRecordId: record?.id ?? recordId,
		id: recordId,
		entityId: recordId,
		document_id: primaryDocumentId,
		documentId: primaryDocumentId,
		uploadedBy: record?.uploaded_by ?? "",
		title,
		fileName: title,
		entityName: title,
		createdDate,
		updatedDate: createdDate,
		conditionId: primaryConditionId,
		condition_id: primaryConditionId,
		conditionName: primaryConditionName,
		condition_name: primaryConditionName,
		conditionType: primaryConditionType,
		condition_type: primaryConditionType,
		conditionIds,
		conditionNames,
		conditionTypes,
		condition_mappings: conditionMappings,
		entityType: primaryEntityType,
		entity_type: primaryEntityType,
		efolderId: primaryEfolderId,
		efolder_id: primaryEfolderId,
		loanType: record?.loan_type ?? "",
		loan_type: record?.loan_type ?? "",
		uploadStatus: record?.upload_status ?? "",
		upload_status: record?.upload_status ?? "",
		retryCount: record?.retry_count ?? 0,
		retry_count: record?.retry_count ?? 0,
		lastRetryTime: record?.last_retry_time ?? null,
		last_retry_time: record?.last_retry_time ?? null,
		s3Url: record?.s3_url ?? "",
		s3_url: record?.s3_url ?? "",
		attachmentFilename: record?.attachment_filename ?? title,
		attachment_filename: record?.attachment_filename ?? title,
		attachmentSize: record?.attachment_size ?? null,
		attachment_size: record?.attachment_size ?? null,
		contentType: record?.content_type ?? "",
		content_type: record?.content_type ?? "",
		createdBy: { entityId: record?.uploaded_by ?? "" },
		assignedTo: [],
		hasConditions: conditionMappings.length > 0,
		__sourceRecord: record,
	};
};

const normalizePendingRetryDocuments = (records = [], source = "enhanced") =>
	(records ?? [])
		.map((record) => buildPendingRetryTrackerDocument(record, source))
		.filter(Boolean);

const mergeTrackerDocuments = (
	serverDocuments = [],
	uploadProgressInfo = null
) => {
	const mergedDocuments = [...(serverDocuments ?? [])];

	if (!uploadProgressInfo) {
		return sortDocumentsByCreatedDateDesc(mergedDocuments);
	}

	const optimisticDocument = buildOptimisticTrackerDocument(uploadProgressInfo);
	const optimisticKey = getTrackerDocumentKey(optimisticDocument);
	const optimisticFingerprint =
		getTrackerDocumentFingerprint(optimisticDocument);

	const matchingIndex = mergedDocuments.findIndex((document) => {
		const documentKey = getTrackerDocumentKey(document);
		return (
			(optimisticKey && documentKey && documentKey === optimisticKey) ||
			(optimisticFingerprint &&
				optimisticFingerprint === getTrackerDocumentFingerprint(document))
		);
	});

	if (matchingIndex >= 0) {
		const matchedDocument = mergedDocuments[matchingIndex];
		mergedDocuments[matchingIndex] = {
			...optimisticDocument,
			...matchedDocument,
			__isProcessingRow: false,
			__stableUploadKey: optimisticKey || matchedDocument?.__stableUploadKey,
			id: optimisticKey || matchedDocument?.id,
			entityId:
				optimisticKey || matchedDocument?.entityId || matchedDocument?.id,
		};
	} else {
		mergedDocuments.unshift(optimisticDocument);
	}

	return sortDocumentsByCreatedDateDesc(mergedDocuments);
};

const JOB_STATUS_POLL_INTERVAL_MS = 3000;
const JOB_STATUS_MAX_POLL_ATTEMPTS = 20;
const RETRY_PENDING_COOLDOWN_MS = 60_000;

const DocumentsTrackerCard = ({
	toggleDrawer,
	handleTabChange,
	fieldData,
	loanGuid,
	setSelectedCondition,
	handleOpenLoadingDataModal,
	handleCloseLoadingDataModal,
	setLoadingDataText,
	handleOpenErrorModal,
	conditionTypeOptions = [],
	refreshConditionTypeOptions,
	isConditionStatusLoaded = false,
	uploadProgressInfo = null,
	setDocumentTrackerUploadProgress = () => {},
}) => {
	const params = useParams();

	const [enhancedConditionsEfolderDocumentsList] =
		useEnhancedConditionsEfolderDocumentsListMutation();
	const [enhancedConditionsDocumentsList] =
		useEnhancedConditionsDocumentsListMutation();
	const [enhancedConditionAttachmentsPendingRetry] =
		useEnhancedConditionAttachmentsPendingRetryMutation();
	const [standardDocumentUploadRetriesPendingRetry] =
		useStandardDocumentUploadRetriesPendingRetryMutation();
	const [exportJob] = useExportJobMutation();
	const [jobStatus] = useJobStatusMutation();
	const [retryPendingDocumentUploads] = useRetryPendingDocumentUploadsMutation();
	const [isRetryingPending, setIsRetryingPending] = useState(false);
	const [retryStatusAlert, setRetryStatusAlert] = useState(null);
	const [retryCooldownUntil, setRetryCooldownUntil] = useState(null);
	const [retryCooldownSecondsLeft, setRetryCooldownSecondsLeft] = useState(0);
	const lastRequestedLoanIdRef = useRef(null);

	useEffect(() => {
		if (!retryCooldownUntil) return undefined;

		const tick = () => {
			const secondsLeft = Math.max(
				0,
				Math.ceil((retryCooldownUntil - Date.now()) / 1000)
			);
			setRetryCooldownSecondsLeft(secondsLeft);
			if (secondsLeft === 0) {
				setRetryCooldownUntil(null);
			}
		};

		tick();
		const intervalId = setInterval(tick, 1000);
		return () => clearInterval(intervalId);
	}, [retryCooldownUntil]);

	const brokerUploadColumns = [
		{
			id: "document-name",
			label: "File Name",
			minWidth: 260,
			sortField: "title",
		},
		{
			id: "upload-date",
			label: "Uploaded (ET)",
			minWidth: 165,
			sortField: "updatedDate",
		},
		{
			id: "user-name",
			label: "Uploaded By",
			minWidth: 200,
			sortField: "createdBy[entityId]",
		},
		{
			id: "logan-status",
			label: "Logan Status",
			minWidth: 200,
			sortField: "statusLabel",
		},
		{
			id: "encompass-doc",
			label: "Encompass Doc",
			minWidth: 260,
			sortField: "eFolderStatus",
		},
		{
			id: "pipeline",
			label: "Pipeline",
			minWidth: 200,
			sortField: "createdBy[entityId]",
		},
		{
			id: "condition-type",
			label: "Cond.",
			minWidth: 100,
			sortField: "matchingConditionCount",
		},
		{
			id: "actions",
			label: "Actions",
			minWidth: 100,
		},
	];

	const [selectedRow, setSelectedRow] = useState([]);
	const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
	const [nonViewableDocument, setNonViewableDocument] = useState(null);
	const [selectedConditionTypes, setSelectedConditionTypes] = useState({});
	const [activeDocumentTab, setActiveDocumentTab] = useState("all");
	const [activeTrackerView, setActiveTrackerView] = useState("conditions");
	const previousTrackerViewRef = useRef(activeTrackerView);
	const [conditionDocuments, setConditionDocuments] = useState([]);
	const [isEnhancedDocumentsLoading, setIsEnhancedDocumentsLoading] =
		useState(false);

	const [searchValue, setSearchValue] = useState({
		title: "",
	});

	const [documentsList, setDocumentsList] = useState([]);
	const isSearchActive = Boolean(normalizeSearchText(searchValue?.title));
	const mergedDocumentsList = useMemo(
		() => mergeTrackerDocuments(documentsList, uploadProgressInfo),
		[documentsList, uploadProgressInfo]
	);
	const documentConditionMatchInfo = useMemo(() => {
		const countsByDocumentKey = new Map();
		let totalDocumentsWithConditions = 0;

		mergedDocumentsList.forEach((document) => {
			const { matchingConditionCount } = getTrackerMatchingConditionInfo(
				document,
				conditionDocuments
			);
			const count = Number(matchingConditionCount ?? 0);
			const documentKey = getTrackerDocumentKey(document);

			if (documentKey) {
				countsByDocumentKey.set(documentKey, count);
			}

			if (count > 0) {
				totalDocumentsWithConditions += 1;
			}
		});

		return {
			countsByDocumentKey,
			totalDocumentsWithConditions,
		};
	}, [conditionDocuments, mergedDocumentsList]);

	const filteredDocumentsList = useMemo(() => {
		const searchedDocuments = mergedDocumentsList?.filter((item) =>
			buildDocumentSearchHaystack(item, conditionDocuments).includes(
				normalizeSearchText(searchValue?.title)
			)
		);

		const getMatchingConditionCount = (item) =>
			documentConditionMatchInfo.countsByDocumentKey.get(
				getTrackerDocumentKey(item)
			) ?? 0;

		const tabbedDocuments = getDocumentsByTab(
			searchedDocuments,
			activeDocumentTab,
			selectedConditionTypes,
			getMatchingConditionCount
		);

		return sortDocumentsByCreatedDateDesc(tabbedDocuments);
	}, [
		activeDocumentTab,
		conditionDocuments,
		documentConditionMatchInfo.countsByDocumentKey,
		mergedDocumentsList,
		searchValue,
		selectedConditionTypes,
	]);

	const finalDocumentsList = useMemo(
		() =>
			sortDocumentsByCreatedDateDesc(
				filterDuplicateFileNameDocuments(
					filteredDocumentsList,
					conditionDocuments
				)
			),
		[conditionDocuments, filteredDocumentsList]
	);
	const totalUploadedCount = finalDocumentsList.length;
	const fullyReceivedCount = finalDocumentsList.filter((row) =>
		isFullyReceivedByLogan(row, conditionDocuments)
	).length;
	const processingCount = finalDocumentsList.filter((item) =>
		isProcessingSummaryDocument(item, conditionDocuments)
	).length;
	const totalWithConditionsCount = finalDocumentsList.filter(
		(item) =>
			(documentConditionMatchInfo.countsByDocumentKey.get(
				getTrackerDocumentKey(item)
			) ?? 0) > 0
	).length;

	const selectedDocumentRows = useMemo(() => {
		if (
			!Array.isArray(selectedDocumentIds) ||
			selectedDocumentIds.length === 0
		) {
			return [];
		}

		return mergedDocumentsList.filter((document) =>
			selectedDocumentIds.includes(getTrackerDocumentSelectionId(document))
		);
	}, [mergedDocumentsList, selectedDocumentIds]);

	useEffect(() => {
		const validSelectionIds = new Set(
			mergedDocumentsList
				.map((document) => getTrackerDocumentSelectionId(document))
				.filter(Boolean)
		);

		setSelectedDocumentIds((previousIds) =>
			previousIds.length === 0
				? previousIds
				: (() => {
						const nextIds = previousIds.filter((id) =>
							validSelectionIds.has(id)
						);

						if (
							nextIds.length === previousIds.length &&
							nextIds.every((id, index) => id === previousIds[index])
						) {
							return previousIds;
						}

						return nextIds;
					})()
		);
	}, [mergedDocumentsList]);

	const handleToggleDocumentSelection = useCallback((document) => {
		const documentSelectionId = getTrackerDocumentSelectionId(document);

		if (!documentSelectionId) {
			return;
		}

		setSelectedDocumentIds((previousIds) => {
			if (previousIds.includes(documentSelectionId)) {
				return previousIds.filter((id) => id !== documentSelectionId);
			}

			return [...previousIds, documentSelectionId];
		});
	}, []);

	const handleToggleAllDocumentSelections = useCallback(
		(documents = [], shouldSelect = false) => {
			if (!Array.isArray(documents) || documents.length === 0) {
				if (!shouldSelect) {
					setSelectedDocumentIds([]);
				}

				return;
			}

			const visibleDocumentIds = documents
				.filter((document) => !document?.__isPendingRetryRecord)
				.map((document) => getTrackerDocumentSelectionId(document))
				.filter(Boolean);

			if (shouldSelect) {
				setSelectedDocumentIds((previousIds) => {
					const nextIds = [...previousIds];
					const seenIds = new Set(previousIds);

					visibleDocumentIds.forEach((documentId) => {
						if (!seenIds.has(documentId)) {
							seenIds.add(documentId);
							nextIds.push(documentId);
						}
					});

					return nextIds;
				});
				return;
			}

			setSelectedDocumentIds([]);
		},
		[]
	);

	const handleErrorModal = useCallback(
		(errorKey, data, { closeLoading = true } = {}) => {
			if (closeLoading) {
				handleCloseLoadingDataModal?.();
			}
			handleOpenErrorModal?.(errorKey, data?.data);
		},
		[handleCloseLoadingDataModal, handleOpenErrorModal]
	);

	const documentTabs = [
		{ key: "all", label: "All Documents" },
		{ key: "in-progress", label: "In Progress" },
		{ key: "has-conditions", label: "Has Conditions" },
	];

	const loadDocuments = useCallback(async () => {
		if (!params?.id) return;

		setIsEnhancedDocumentsLoading(true);
		handleOpenLoadingDataModal?.();
		setLoadingDataText?.("Setting up the documents list");

		try {
			const [
				efolderResponse,
				conditionDocumentsResponse,
				enhancedPendingRetryResponse,
				standardPendingRetryResponse,
			] = await Promise.all([
				enhancedConditionsEfolderDocumentsList({
					loanGuid: params?.id,
				}).unwrap(),
				getEnhancedConditionsDocumentsList({
					loanGuid: params?.id,
					apiRequest: enhancedConditionsDocumentsList,
					handleCloseModal: handleCloseLoadingDataModal,
					handleErrorModal,
				}),
				// Pending-retry rows are supplementary - a failure here must not
				// block the primary document list from loading.
				enhancedConditionAttachmentsPendingRetry({
					loanGuid: params?.id,
				})
					.unwrap()
					.catch(() => ({ data: [] })),
				standardDocumentUploadRetriesPendingRetry({
					loanGuid: params?.id,
				})
					.unwrap()
					.catch(() => ({ data: [] })),
			]);

			setConditionDocuments(conditionDocumentsResponse?.data || []);
			const efolderDocuments = (efolderResponse?.data || []).filter(
				(document) =>
					!(document.isActive === false && document.isRemoved === false)
			);
			const pendingRetryDocuments = [
				...normalizePendingRetryDocuments(
					enhancedPendingRetryResponse?.data || [],
					"enhanced"
				),
				...normalizePendingRetryDocuments(
					standardPendingRetryResponse?.data || [],
					"standard"
				),
			];

			setDocumentsList([...efolderDocuments, ...pendingRetryDocuments]);
		} catch (err) {
			handleErrorModal("ENHANCED_CONDITION_DOCUMENTS_UNAVAILABLE", err);
			throw err;
		} finally {
			setIsEnhancedDocumentsLoading(false);
			handleCloseLoadingDataModal?.();
		}
	}, [
		params?.id,
		enhancedConditionsEfolderDocumentsList,
		enhancedConditionsDocumentsList,
		enhancedConditionAttachmentsPendingRetry,
		standardDocumentUploadRetriesPendingRetry,
		handleOpenLoadingDataModal,
		handleCloseLoadingDataModal,
		setLoadingDataText,
		handleErrorModal,
	]);

	const handleRefreshTracker = useCallback(async () => {
		await loadDocuments().catch(() => {});
		await refreshConditionTypeOptions?.().catch(() => {});
	}, [loadDocuments, refreshConditionTypeOptions]);

	// The pending-retry list endpoints already filter by uploaded_by on the
	// backend, so every __isPendingRetryRecord row already in documentsList is
	// this user's own - no extra current-user lookup needed here.
	const myPendingRetryCount = useMemo(
		() => documentsList.filter((document) => document?.__isPendingRetryRecord).length,
		[documentsList]
	);

	const handleRetryPendingUploads = useCallback(async () => {
		if (!params?.id || myPendingRetryCount === 0) return;

		setIsRetryingPending(true);
		setRetryStatusAlert(null);
		try {
			const response = await retryPendingDocumentUploads({
				loanGuid: params.id,
			}).unwrap();
			const queued = Boolean(response?.data?.queued);

			setRetryStatusAlert(
				queued
					? {
							severity: "success",
							subject: "Retry started",
							details:
								"We're retrying your pending document(s). This will continue in the background. Check the Documents list for the latest status. If needed, you can retry again in 1 minute.",
						}
					: {
							severity: "info",
							subject: "Nothing to retry",
							details: "There are no pending documents to retry right now.",
						}
			);
			setRetryCooldownUntil(Date.now() + RETRY_PENDING_COOLDOWN_MS);
			await handleRefreshTracker();
		} catch (err) {
			handleErrorModal("DOCUMENT_RETRY_TRIGGER_FAILED", err);
		} finally {
			setIsRetryingPending(false);
		}
	}, [
		params?.id,
		myPendingRetryCount,
		retryPendingDocumentUploads,
		handleRefreshTracker,
		handleErrorModal,
	]);

	useEffect(() => {
		if (!params?.id) return;
		if (lastRequestedLoanIdRef.current === params?.id) return;
		lastRequestedLoanIdRef.current = params?.id;

		void loadDocuments().catch(() => {});
	}, [params?.id, loadDocuments]);

	useEffect(() => {
		const previousTrackerView = previousTrackerViewRef.current;

		if (
			previousTrackerView === "conditions" &&
			activeTrackerView === "documents"
		) {
			void loadDocuments().catch(() => {});
		}

		previousTrackerViewRef.current = activeTrackerView;
	}, [activeTrackerView, loadDocuments]);

	useEffect(() => {
		if (!uploadProgressInfo) {
			return;
		}

		const uploadKey = getTrackerDocumentKey(uploadProgressInfo);
		if (!uploadKey) {
			return;
		}

		const confirmedMatch = mergedDocumentsList.find(
			(document) =>
				getTrackerDocumentKey(document) === uploadKey &&
				!document?.__isProcessingRow
		);

		if (confirmedMatch) {
			setDocumentTrackerUploadProgress?.(null);
		}
	}, [
		mergedDocumentsList,
		setDocumentTrackerUploadProgress,
		uploadProgressInfo,
	]);

	const handleSearchTable = (value, name) => {
		const updatedSearchValue = { ...searchValue, [name]: value };
		setSearchValue(updatedSearchValue);
	};

	const handleClearSearch = () => {
		setSearchValue({ title: "" });
	};

	const getExportedAttachmentBlob = useCallback(
		async ({ entityUri, authorizationHeader }) => {
			if (!entityUri) {
				throw new Error("Exported file URI is missing");
			}

			if (!authorizationHeader) {
				throw new Error(
					"Authorization header is missing for the exported file"
				);
			}

			const response = await fetch(entityUri, {
				method: "GET",
				headers: {
					Authorization: authorizationHeader,
				},
			});

			if (!response.ok) {
				const fallbackMessage = "Unable to fetch the exported attachment";
				let errorMessage = fallbackMessage;

				try {
					const responseText = await response.text();
					if (responseText?.trim()) {
						errorMessage = responseText;
					}
				} catch (error) {
					// Keep the fallback message.
				}

				throw new Error(errorMessage);
			}

			return response.blob();
		},
		[]
	);

	const openBlobInNewTab = useCallback((blob) => {
		const objectUrl = window.URL.createObjectURL(blob);
		const tab = window.open(objectUrl, "_blank");

		tab?.focus?.();
		window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
	}, []);

	const downloadBlob = useCallback((blob, fileName = "download") => {
		const objectUrl = window.URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = objectUrl;
		link.download = fileName || "download";
		link.rel = "noopener";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
	}, []);

	const handleJobStatus = useCallback(
		async (jobId) => {
			for (
				let attempt = 0;
				attempt < JOB_STATUS_MAX_POLL_ATTEMPTS;
				attempt += 1
			) {
				const jobStatusData = await jobStatus({
					job_id: jobId,
				}).unwrap();

				const status = String(jobStatusData?.response?.status ?? "")
					.trim()
					.toLowerCase();

				if (status.includes("queue") || status.includes("running")) {
					if (attempt < JOB_STATUS_MAX_POLL_ATTEMPTS - 1) {
						await new Promise((resolve) =>
							setTimeout(resolve, JOB_STATUS_POLL_INTERVAL_MS)
						);
						continue;
					}

					throw new Error("Job status polling timed out");
				}

				return jobStatusData;
			}

			throw new Error("Job status polling timed out");
		},
		[jobStatus]
	);

	const handleExportedDocument = useCallback(
		async (document, action, { showLoading = true } = {}) => {
			const attachmentId = document?.entityId ?? document?.id;

			if (!attachmentId) {
				handleErrorModal(
					action === "view" ? "DOCUMENT_VIEW_ERROR" : "DOCUMENT_DOWNLOAD_ERROR",
					null,
					{ closeLoading: showLoading }
				);
				return false;
			}

			if (showLoading) {
				setLoadingDataText?.(
					action === "view" ? "Opening document" : "Downloading document"
				);
				handleOpenLoadingDataModal?.();
			}

			try {
				const knownFileName =
					document?.entityName ?? document?.fileName ?? document?.title ?? "";

				const formData = new FormData();
				formData.append("attachment_ids", attachmentId);
				if (knownFileName) {
					// Lets the backend detect non-PDF attachments (e.g. .txt) and
					// return the original file instead of Encompass's PDF
					// "transformation" of it - see apply_original_file_when_not_pdf
					// in loan/api_v1/views.py (cb_portal_backend).
					formData.append("file_name", knownFileName);
				}

				const createExportJobResponse = await exportJob({
					loanGuid: params?.id,
					formData,
				}).unwrap();

				const exportJobResponse =
					createExportJobResponse?.response ?? createExportJobResponse;
				const exportJobId =
					exportJobResponse?.jobId ?? exportJobResponse?.file?.entityId;

				if (!exportJobId) {
					console.error(
						"Export job identifier is missing. Skipping job status request."
					);
					handleErrorModal(
						action === "view"
							? "DOCUMENT_VIEW_ERROR"
							: "DOCUMENT_DOWNLOAD_ERROR",
						"Export job identifier is missing",
						{ closeLoading: showLoading }
					);
					return false;
				}

				const downloadUrlResponse = await handleJobStatus(exportJobId);

				const statusResponse =
					downloadUrlResponse?.response ?? downloadUrlResponse;
				const fileInfo = statusResponse?.file ?? {};
				const entityUri = fileInfo?.entityUri;
				const authorizationHeader = fileInfo?.authorizationHeader;
				const contentType = fileInfo?.contentType;
				const fileName = knownFileName || "download";

				if (!entityUri || !authorizationHeader) {
					handleErrorModal(
						action === "view"
							? "DOCUMENT_VIEW_ERROR"
							: "DOCUMENT_DOWNLOAD_ERROR",
						"The exported file is missing download details",
						{ closeLoading: showLoading }
					);
					return false;
				}

				const blob = await getExportedAttachmentBlob({
					entityUri,
					authorizationHeader,
				});

				if (action === "view") {
					openBlobInNewTab(blob);
					return true;
				}

				downloadBlob(blob, fileName || contentType || "download");
				return true;
			} catch (err) {
				handleErrorModal(
					action === "view" ? "DOCUMENT_VIEW_ERROR" : "DOCUMENT_DOWNLOAD_ERROR",
					err,
					{ closeLoading: showLoading }
				);
				return false;
			} finally {
				if (showLoading) {
					handleCloseLoadingDataModal?.();
				}
			}
		},
		[
			handleErrorModal,
			exportJob,
			handleJobStatus,
			getExportedAttachmentBlob,
			downloadBlob,
			handleCloseLoadingDataModal,
			handleOpenLoadingDataModal,
			openBlobInNewTab,
			params?.id,
			setLoadingDataText,
		]
	);

	const handleExportSelectedDocuments = useCallback(async () => {
		if (!selectedDocumentRows.length) {
			return;
		}

		const totalDocuments = selectedDocumentRows.length;
		setLoadingDataText?.(`Downloading 1 of ${totalDocuments} Documents`);
		handleOpenLoadingDataModal?.();

		try {
			let allDownloadsSucceeded = true;

			for (const [index, document] of selectedDocumentRows.entries()) {
				setLoadingDataText?.(
					`Downloading ${index + 1} of ${totalDocuments} Documents`
				);

				const downloadSucceeded = await handleExportedDocument(
					document,
					"download",
					{
						showLoading: false,
					}
				);

				if (!downloadSucceeded) {
					allDownloadsSucceeded = false;
				}
			}

			if (allDownloadsSucceeded) {
				setSelectedDocumentIds([]);
			}
		} finally {
			handleCloseLoadingDataModal?.();
		}
	}, [
		handleCloseLoadingDataModal,
		handleExportedDocument,
		handleOpenLoadingDataModal,
		selectedDocumentRows,
		setLoadingDataText,
	]);

	const handleDownloadDocument = useCallback(
		async (document) => {
			await handleExportedDocument(document, "download");
		},
		[handleExportedDocument]
	);

	const handleViewDocument = useCallback(
		async (document, isRenderable = true) => {
			if (!isRenderable) {
				setNonViewableDocument(document);
				return;
			}

			await handleExportedDocument(document, "view");
		},
		[handleExportedDocument]
	);

	const handleNonViewableDownload = useCallback(async () => {
		if (!nonViewableDocument) {
			return;
		}

		const document = nonViewableDocument;
		setNonViewableDocument(null);
		await handleDownloadDocument(document);
	}, [handleDownloadDocument, nonViewableDocument]);

	const trackerSubtitle =
		activeTrackerView === "documents"
			? "Viewing documents submitted through Logan Connect"
			: "Viewing conditions linked to loan documents";
	const [lastSyncedText] = useState(() => formatEasternTimestamp());
	const hasUploadProgressRow = Boolean(
		uploadProgressInfo?.fileName ||
			uploadProgressInfo?.time ||
			uploadProgressInfo?.uploadedBy ||
			uploadProgressInfo?.status
	);
	const hasNoDocuments =
		finalDocumentsList.length === 0 && !hasUploadProgressRow;
	const showDocumentsLoader =
		activeTrackerView === "documents" && isEnhancedDocumentsLoading;
	const showBlockingLoader = showDocumentsLoader && hasNoDocuments;
	const showInlineRefresh = showDocumentsLoader && !showBlockingLoader;
	const visibleUploadedCount = finalDocumentsList.length;
	const visibleFullyReceivedCount = finalDocumentsList.filter((row) =>
		isFullyReceivedByLogan(row, conditionDocuments)
	).length;
	const visibleProcessingCount = finalDocumentsList.filter((item) =>
		isProcessingSummaryDocument(item, conditionDocuments)
	).length;
	const visibleWithConditionsCount = finalDocumentsList.filter(
		(item) =>
			(documentConditionMatchInfo.countsByDocumentKey.get(
				getTrackerDocumentKey(item)
			) ?? 0) > 0
	).length;
	const formatSummaryCount = (visibleCount, totalCount) =>
		isSearchActive ? `${visibleCount} of ${totalCount}` : String(totalCount);

	const handleTrackerTabChange = (event, newValue) => {
		setActiveTrackerView(newValue === 0 ? "documents" : "conditions");
	};

	const summaryCards = [
		{
			label: "Total Uploaded",
			value: formatSummaryCount(visibleUploadedCount, totalUploadedCount),
			icon: DescriptionOutlinedIcon,
			className: styles.summaryCardPrimary,
		},
		{
			label: "Fully Received",
			value: formatSummaryCount(visibleFullyReceivedCount, fullyReceivedCount),
			icon: CheckOutlinedIcon,
			className: styles.summaryCardSuccess,
		},
		{
			label: "Processing",
			value: formatSummaryCount(visibleProcessingCount, processingCount),
			icon: AccessTimeOutlinedIcon,
			className: styles.summaryCardLight,
		},
		{
			label: "With Conditions",
			value: formatSummaryCount(
				visibleWithConditionsCount,
				totalWithConditionsCount
			),
			icon: AssignmentTurnedInOutlinedIcon,
			className: styles.summaryCardLight,
		},
	];

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				className={styles.topContainer}
			>
				<Stack direction="column" className={styles.headerTitleGroup}>
					<Heading3
						text="Condition & Document Tracker"
						extraClass={styles.tabTitle}
						fontType="semibold"
					/>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					className={styles.topBtnContainer}
				>
					<Stack
						direction="column"
						alignItems="flex-end"
						className={styles.syncMeta}
					>
						<Body2
							text="Last synced"
							extraClass={styles.syncLabel}
							fontType="semibold"
						/>
						<Body2
							text={lastSyncedText}
							extraClass={styles.syncValue}
							fontType="semibold"
						/>
					</Stack>
					<Box
						component="button"
						type="button"
						className={styles.refreshButton}
						aria-label="Refresh document tracker"
						title="Refresh"
						onClick={handleRefreshTracker}
					>
						<span className={styles.refreshButtonText}>Refresh</span>
					</Box>
					<Box
						component="button"
						type="button"
						className={styles.refreshButton}
						aria-label="Retry my pending document uploads for this loan"
						title={
							retryCooldownSecondsLeft > 0
								? `You can retry again in ${retryCooldownSecondsLeft}s`
								: "Retry your own pending document uploads for this loan"
						}
						onClick={handleRetryPendingUploads}
						disabled={
							myPendingRetryCount === 0 ||
							isRetryingPending ||
							retryCooldownSecondsLeft > 0
						}
					>
						<span className={styles.refreshButtonText}>
							{isRetryingPending
								? "Retrying..."
								: `Retry Pending (${myPendingRetryCount})`}
						</span>
					</Box>
					<Box
						component="button"
						type="button"
						className={styles.glossaryButton}
						aria-label="Open glossary"
						title="Glossary"
						onClick={(event) => toggleDrawer?.(event, "glossary")}
					>
						<MenuBookOutlinedIcon className={styles.glossaryIcon} />
						<span className={styles.glossaryButtonText}>Glossary</span>
					</Box>
					<TertiaryButton
						startIcon={<CloseIcon />}
						onClick={(e) => handleTabChange(e, false)}
					/>
				</Stack>
			</Stack>
			{retryStatusAlert ? (
				<CustomAlert
					severity={retryStatusAlert.severity}
					subject={retryStatusAlert.subject}
					details={retryStatusAlert.details}
					onClose={() => setRetryStatusAlert(null)}
				/>
			) : null}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				className={styles.trackerControlBar}
			>
				<Stack
					direction="row"
					alignItems="center"
					className={styles.viewToggleGroup}
				>
					<Tabs
						value={activeTrackerView === "documents" ? 0 : 1}
						onChange={handleTrackerTabChange}
						aria-label="Tracker view tabs"
						className={styles.trackerTabs}
					>
						<Tab
							label={
								<Stack direction="row" alignItems="center" gap={1}>
									<DescriptionOutlinedIcon className={styles.trackerTabIcon} />
									<span>Documents</span>
								</Stack>
							}
							value={0}
							className={styles.trackerTab}
						/>
						<Tab
							label={
								<Stack direction="row" alignItems="center" gap={1}>
									<AssignmentTurnedInOutlinedIcon
										className={styles.trackerTabIcon}
									/>
									<span>Conditions</span>
								</Stack>
							}
							value={1}
							className={styles.trackerTab}
						/>
					</Tabs>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					className={styles.actionGroup}
				>
					<Box component="span" className={styles.headerSubtitle}>
						{trackerSubtitle}
					</Box>
					{activeTrackerView === "documents" && (
						<Stack direction="row" alignItems="center" gap={1.5}>
							<Body2
								text={`${selectedDocumentRows.length} Documents Selected`}
								extraClass={styles.documentsCountText}
								fontType="semibold"
							/>
							<PrimaryButton
								text="Export All / Download"
								onClick={handleExportSelectedDocuments}
								disabled={selectedDocumentRows.length === 0}
								extraClass=""
								extraTextClass=""
							/>
							<PrimaryButton
								text="Upload Documents"
								startIcon={
									<FileUploadOutlinedIcon
										className={styles.uploadButtonIcon}
										sx={{
											color: "#FFFFFF",
											fill: "#FFFFFF",
											stroke: "#FFFFFF",
										}}
									/>
								}
								extraClass={styles.uploadDocumentsButton}
								extraTextClass={styles.uploadDocumentsButtonText}
								onClick={(e) =>
									toggleDrawer(e, "upload-condition-tracker-documents", {
										nextConditionsOptions: conditionTypeOptions,
										onConditionListRefresh: refreshConditionTypeOptions,
										onUploadSuccess: loadDocuments,
									})
								}
								disabled={selectedRow.length > 0}
							/>
						</Stack>
					)}
				</Stack>
			</Stack>
			{activeTrackerView === "documents" && (
				<Stack direction="row" className={styles.summaryContainer}>
					{summaryCards.map((card) => (
						<Box
							key={card.label}
							className={`${styles.summaryCard} ${card.className}`}
						>
							<card.icon className={styles.summaryIcon} />
							<Stack direction="row" alignItems="center" gap={1}>
								<Heading3
									text={String(card.value)}
									extraClass={styles.summaryValue}
									fontType="bold"
								/>
								{isSearchActive ? (
									<Box className={styles.summaryFilteredBadge}>Filtered</Box>
								) : null}
							</Stack>
							<Body2
								text={card.label}
								extraClass={styles.summaryLabel}
								fontType="semibold"
							/>
						</Box>
					))}
				</Stack>
			)}
			{activeTrackerView === "documents" && (
				<Stack direction="row" className={styles.filterBar}>
					<Stack direction="row" className={styles.filterTabs}>
						{documentTabs.map((tab) => {
							const isActive = activeDocumentTab === tab.key;

							return (
								<Box
									key={tab.key}
									className={`${styles.filterTab} ${
										isActive ? styles.filterTabActive : ""
									}`}
									onClick={() => setActiveDocumentTab(tab.key)}
								>
									<Body2
										text={tab.label}
										extraClass={styles.filterTabLabel}
										fontType="semibold"
									/>
								</Box>
							);
						})}
					</Stack>

					<Stack
						direction="row"
						alignItems="center"
						className={styles.searchContainer}
					>
						<OutlinedTextInput
							extraClass={styles.searchField}
							variant="outlined"
							placeholder="Search file or Encompass document"
							name="title"
							value={searchValue?.title}
							handleChange={(event) =>
								handleSearchTable(event.target.value, event.target.name)
							}
							showEndIcon={Boolean(searchValue?.title)}
							endIcon={<CloseIcon />}
							endIconAriaLabel="Clear search"
							endIconOnClick={handleClearSearch}
						/>
						<Body2
							text={`${finalDocumentsList.length} documents`}
							extraClass={styles.documentsCountText}
							fontType="semibold"
						/>
					</Stack>
				</Stack>
			)}
			<Fade
				in={activeTrackerView === "documents"}
				timeout={220}
				mountOnEnter
				unmountOnExit
			>
				<Stack direction="row" className={styles.bottomContainer}>
					<Stack direction="column" sx={{ width: "100%", gap: 1.5 }}>
						{showInlineRefresh ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-end",
									minHeight: 24,
									px: 0.5,
								}}
							>
								<Body2
									text="Refreshing documents..."
									extraClass={styles.emptyStateText}
									fontType="semibold"
								/>
							</Box>
						) : null}
						{showBlockingLoader ? (
							<Box className={styles.emptyState}>
								<Stack direction="column" alignItems="center" gap={2}>
									<CustomLoader />
									<Body2
										text="Refreshing documents..."
										extraClass={styles.emptyStateText}
										fontType="semibold"
									/>
								</Stack>
							</Box>
						) : null}
						{hasNoDocuments ? (
							<Box className={styles.emptyState}>
								<Body2
									text="No documents found"
									extraClass={styles.emptyStateText}
									fontType="semibold"
								/>
							</Box>
						) : null}
						{!hasNoDocuments || hasUploadProgressRow || showInlineRefresh ? (
							<CustomTable
								enableSelect={true}
								columnData={brokerUploadColumns}
								rowData={finalDocumentsList}
								tableType="broker-documents-tracker"
								maxHeight={true}
								toggleDrawer={toggleDrawer}
								onRefreshDocumentsList={loadDocuments}
								loanId={params?.id}
								setSortedData={setDocumentsList}
								selectedRow={selectedRow}
								setSelectedRow={setSelectedRow}
								conditionTypeOptions={conditionTypeOptions}
								isConditionStatusLoaded={isConditionStatusLoaded}
								selectedConditionTypes={selectedConditionTypes}
								setSelectedConditionTypes={setSelectedConditionTypes}
								conditionDocuments={conditionDocuments}
								selectedDocumentIds={selectedDocumentIds}
								onToggleDocumentSelection={handleToggleDocumentSelection}
								onToggleAllDocumentSelection={handleToggleAllDocumentSelections}
								onViewDocument={handleViewDocument}
								onDownloadDocument={handleDownloadDocument}
							/>
						) : null}
					</Stack>
				</Stack>
			</Fade>
			<Fade
				in={activeTrackerView === "conditions"}
				timeout={220}
				mountOnEnter
				unmountOnExit
			>
				<Box className={styles.conditionsViewContainer}>
					<ConditionTrackingCard
						toggleDrawer={toggleDrawer}
						fieldData={fieldData}
						handleOpenLoadingDataModal={handleOpenLoadingDataModal}
						handleCloseLoadingDataModal={handleCloseLoadingDataModal}
						setLoadingDataText={setLoadingDataText}
						setSelectedCondition={setSelectedCondition}
					/>
				</Box>
			</Fade>

			<DocumentInfoModal
				open={Boolean(nonViewableDocument)}
				text="This document cannot be viewed in the browser. Would you like to download the file instead?"
				confirmText="Download"
				handleConfirm={handleNonViewableDownload}
				closeText="Cancel"
				handleClose={() => setNonViewableDocument(null)}
			/>
		</Stack>
	);
};

export default DocumentsTrackerCard;
